import { authorizationHeader, type Session } from '../auth/auth-client';
import { readProblem } from '../net/problem-json';
import type { Equipment } from '../game/world';
import { profileShipFrom, type ProfileShip, type ProfileShipPayload } from './profile-ship';

export interface AccountProfile {
  readonly accountId: number;
  readonly username: string;
  readonly credit: number;
  readonly profileShip: ProfileShip | null;
}

export interface RedeemResult {
  readonly creditsGranted: number;
  readonly productsGranted: readonly number[];
  readonly credit: number;
}

export type AccountErrorKind =
  | 'invalid-request'
  | 'conflict'
  | 'session-invalid'
  | 'rate-limited'
  | 'unreachable'
  | 'not-available'
  | 'server-error';

export class AccountError extends Error {
  override readonly name = 'AccountError';

  constructor(
    message: string,
    readonly kind: AccountErrorKind,
    readonly code: string | null = null,
    readonly fieldErrors: Readonly<Record<string, readonly string[]>> = {},
  ) {
    super(message);
  }
}

/** The API tier has no channel back to the game server, so a mutation here leaves the connected socket holding a stale copy until the client asks it to re-read. */
export interface AccountClient {
  profile(session: Session): Promise<AccountProfile>;
  credit(session: Session): Promise<number>;
  redeem(session: Session, code: string): Promise<RedeemResult>;
  purchases(session: Session): Promise<readonly number[]>;
  cosmetics(session: Session): Promise<readonly EquippedSlot[]>;
  setProfileShip(session: Session, shipId: number): Promise<ProfileShip>;
  purchase(session: Session, productId: number): Promise<PurchaseResult>;
  equip(session: Session, productId: number): Promise<EquipResult>;
}

export interface PurchaseResult {
  readonly productId: number;
  readonly price: number;
  readonly credit: number;
  readonly equippedSlot: number | null;
}

export interface EquipResult {
  readonly slot: number;
  readonly productId: number | null;
}

export interface EquippedSlot {
  readonly slot: number;
  readonly productId: number;
}

/** A slot the response does not mention is empty, so the result is built from zero rather than patched onto whatever was already there. */
export function equipmentFromSlots(slots: readonly EquippedSlot[]): Equipment {
  const shipSkins: number[] = [];
  const equipment: Equipment = { decal: 0, trail: 0, tag: 0, shipSkins };
  for (const { slot, productId } of slots) {
    if (slot === 0) equipment.decal = productId;
    else if (slot === 1) equipment.trail = productId;
    else if (slot === 2) equipment.tag = productId;
    else shipSkins[slot - 3] = productId;
  }
  return equipment;
}

export class HttpAccountClient implements AccountClient {
  constructor(private readonly baseUrl: string) {}

  async profile(session: Session): Promise<AccountProfile> {
    const response = await this.send('/api/account/profile', session, { method: 'GET' });
    if (!response.ok) throw await this.problemToError(response);

    const body = (await response.json()) as Partial<{
      accountId: number;
      username: string;
      credit: number;
      profileShip: ProfileShipPayload | null;
    }>;

    if (typeof body.accountId !== 'number' || typeof body.username !== 'string') {
      throw new AccountError('The server returned a profile without an account.', 'server-error');
    }

    return {
      accountId: body.accountId,
      username: body.username,
      credit: body.credit ?? 0,
      profileShip: profileShipFrom(body.profileShip),
    };
  }

  async setProfileShip(session: Session, shipId: number): Promise<ProfileShip> {
    const response = await this.send('/api/account/profile-ship', session, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ shipId }),
    });
    if (!response.ok) throw await this.problemToError(response);

    const captured = profileShipFrom((await response.json()) as ProfileShipPayload);
    if (captured === null) {
      throw new AccountError('The server did not say what it captured.', 'server-error');
    }
    return captured;
  }

  async credit(session: Session): Promise<number> {
    const response = await this.send('/api/account/credit', session, { method: 'GET' });
    if (!response.ok) throw await this.problemToError(response);

    const body = (await response.json()) as Partial<{ credit: number }>;
    return body.credit ?? 0;
  }

  async redeem(session: Session, code: string): Promise<RedeemResult> {
    const response = await this.send('/api/account/redeem', session, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    if (!response.ok) throw await this.problemToError(response);

    const body = (await response.json()) as Partial<{
      creditsGranted: number;
      productsGranted: number[];
      credit: number;
    }>;

    return {
      creditsGranted: body.creditsGranted ?? 0,
      productsGranted: body.productsGranted ?? [],
      credit: body.credit ?? 0,
    };
  }

  async purchase(session: Session, productId: number): Promise<PurchaseResult> {
    const response = await this.send('/api/account/purchase', session, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ productId }),
    });
    if (!response.ok) throw await this.problemToError(response);

    const body = (await response.json()) as Partial<{
      productId: number;
      price: number;
      credit: number;
      equippedSlot: number | null;
    }>;

    return {
      productId: body.productId ?? productId,
      price: body.price ?? 0,
      credit: body.credit ?? 0,
      equippedSlot: body.equippedSlot ?? null,
    };
  }

  async equip(session: Session, productId: number): Promise<EquipResult> {
    const response = await this.send('/api/account/equip', session, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ productId }),
    });
    if (!response.ok) throw await this.problemToError(response);

    const body = (await response.json()) as Partial<{ slot: number; productId: number | null }>;
    return { slot: body.slot ?? 0, productId: body.productId ?? null };
  }

  async purchases(session: Session): Promise<readonly number[]> {
    const response = await this.send('/api/account/purchases', session, { method: 'GET' });
    if (!response.ok) throw await this.problemToError(response);

    const body = (await response.json()) as Partial<{ owned: { productId?: number }[] }>;
    return (body.owned ?? [])
      .map((row) => row.productId)
      .filter((id): id is number => typeof id === 'number');
  }

  async cosmetics(session: Session): Promise<readonly EquippedSlot[]> {
    const response = await this.send('/api/account/cosmetics', session, { method: 'GET' });
    if (!response.ok) throw await this.problemToError(response);

    const body = (await response.json()) as Partial<{
      equipped: { slot?: number; productId?: number }[];
    }>;
    return (body.equipped ?? [])
      .filter(
        (row): row is { slot: number; productId: number } =>
          typeof row.slot === 'number' && typeof row.productId === 'number',
      )
      .map((row) => ({ slot: row.slot, productId: row.productId }));
  }

  private async send(
    path: string,
    session: Session,
    init: { method: string; headers?: Record<string, string>; body?: string },
  ): Promise<Response> {
    try {
      return await fetch(`${this.baseUrl.replace(/\/+$/, '')}${path}`, {
        method: init.method,
        ...(init.body !== undefined ? { body: init.body } : {}),
        credentials: 'omit',
        headers: { ...init.headers, authorization: authorizationHeader(session) },
      });
    } catch (cause) {
      const error = new AccountError(
        'Could not reach the server. Check your connection and try again.',
        'unreachable',
      );
      error.cause = cause;
      throw error;
    }
  }

  private async problemToError(response: Response): Promise<AccountError> {
    return problemToAccountError(response);
  }
}

export async function problemToAccountError(response: Response): Promise<AccountError> {
  const problem = await readProblem(response);
  const kind = kindForStatus(response.status);
  return new AccountError(
    kind === 'not-available' ? defaultMessage(kind) : (problem.detail ?? defaultMessage(kind)),
    kind,
    problem.code,
    problem.fieldErrors,
  );
}

export function kindForStatus(status: number): AccountErrorKind {
  if (status === 401 || status === 403) return 'session-invalid';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate-limited';
  if (status === 400 || status === 422) return 'invalid-request';
  if (status === 404) return 'not-available';
  return 'server-error';
}

export function defaultMessage(kind: AccountErrorKind): string {
  switch (kind) {
    case 'session-invalid':
      return 'Your session has expired. Log in again.';
    case 'conflict':
      return 'That could not be completed as it stands.';
    case 'rate-limited':
      return 'Too many attempts. Wait a moment and try again.';
    case 'invalid-request':
      return 'Some of those details were not accepted.';
    case 'unreachable':
      return 'Could not reach the server. Check your connection and try again.';
    case 'not-available':
      return 'That is not available on this server yet.';
    case 'server-error':
      return 'Something went wrong on our side. Try again shortly.';
  }
}
